import { styled as materialStyled } from '@mui/material/styles';
import {
  DialogTitle,
  Dialog,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Checkbox,
  Button,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { CommonButtonV1 } from 'gemlux-common-components';
import CheckSquareUnselectedSvg from '../../../assets/img/check-box-base.svg';
import CheckSquareOutlineSvg from '../../../assets/img/check-box-ticked.svg';
import CloseIconSvg from '../../../assets/img/close-icon.svg';
import WarningSvg from '../../../assets/img/warning-round-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import CustomModal from '../../V1/CustomModal/CustomModal';
import { theme } from '../../../theme/theme';
import SubordinateEmailMultiSelect from '../SubordinateEmailMultiSelect/SubordinateEmailMultiSelect';
import { inviteActions, IPermission } from '../../../redux/invite/inviteSlice';
import { useAuth } from '../../../context/AuthContext';
import { UsageType } from '../../../utils/enums/usage-type.enum';

const CustomDialogTitle = materialStyled(DialogTitle)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));
const CustomDialog = materialStyled(Dialog)(() => ({
  backgroundColor: 'rgba(14, 24, 49, 0.2)',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.05)',
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    maxWidth: '100vw',
    width: '70%',
    height: 'auto',
  },
  padding: '24px',
}));

export interface CustomInvoiceDialogProps {
  open: boolean;
  setIsInviteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PermissionItemProps {
  permissionId: number;
  description: string;
  isChecked: boolean;
  isDisabled: boolean;
  tooltipMessage: string;
  onCheck: (_permissionId: number) => void;
}

const PermissionItem = React.memo(
  ({ permissionId, description, isChecked, isDisabled, tooltipMessage, onCheck }: PermissionItemProps) => (
    <Grid item xs={12} sm={6} key={permissionId}>
      <Tooltip title={isDisabled && tooltipMessage} placement="top" followCursor>
        <Box display="flex" alignItems="center">
          <Checkbox
            inputProps={{ 'aria-label': description }}
            checked={isChecked}
            onChange={() => onCheck(permissionId)}
            checkedIcon={<Box component="img" src={CheckSquareOutlineSvg} />}
            disabled={isDisabled}
            icon={<Box component="img" src={CheckSquareUnselectedSvg} />}
            edge="start"
          />
          <Typography variant="tableRowRegular" sx={{ color: isDisabled ? 'text.disabled' : 'text.primary' }}>
            {description}
          </Typography>
        </Box>
      </Tooltip>
    </Grid>
  ),
);

function InvitationModal({ open, setIsInviteModalOpen }: CustomInvoiceDialogProps) {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { permissions, invitationLoading } = useAppSelector((state: RootState) => state.invite);
  const [error, setError] = useState('');
  const [checkedPermissions, setCheckedPermissions] = useState<number[]>([]);
  const [emailList, setEmailList] = useState<{ id: string; email: string }[]>([]);
  const [isInviteConfirmationModalOpen, setIsInviteConfirmationModalOpen] = useState(false);
  const [isUserLimitExceed, setIsUserLimitExceed] = useState(false);

  const permissionMap = useMemo(() => new Map(permissions?.map((p) => [p.permissionId ?? '', p])), [permissions]);

  interface UserLimitObject {
    limit: number;
    usage: number;
  }

  const userLimitObject: UserLimitObject = user?.usage?.find(
    (item: { type: UsageType }) => item.type === UsageType.USER_LIMIT,
  ) ?? { limit: 0, usage: 0 };
  const userLimit = userLimitObject.limit;
  const subordinateUsage = userLimitObject.usage;
  const isButtonDisabled = error !== '' || emailList.length === 0 || checkedPermissions.length === 0;

  useEffect(() => {
    dispatch(inviteActions.getPermissionsAsync());
  }, []);

  useEffect(() => {
    if (userLimit < subordinateUsage + emailList.length) {
      setIsUserLimitExceed(true);
    } else {
      setIsUserLimitExceed(false);
    }
  }, [userLimit, subordinateUsage, emailList]);

  useEffect(() => {
    if (!invitationLoading) {
      setEmailList([]);
      setCheckedPermissions([]);
      setIsInviteConfirmationModalOpen(false);
    }
  }, [invitationLoading]);

  useEffect(() => {
    if (permissions && user.isNccUser) {
      setCheckedPermissions(permissions.map((p) => p.permissionId));
    }
  }, [permissions, user]);

  const handleCheckboxClick = (permissionId: number) => {
    setCheckedPermissions((prev) => {
      const newCheckedPermissions = prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId];

      return newCheckedPermissions.filter((id) => {
        const permission = permissionMap.get(id);
        const dependencies = permission?.dependencies ?? [];
        if (dependencies.length === 0) return true;

        return dependencies.some((dep) => newCheckedPermissions.includes(dep));
      });
    });
  };

  const handleCloseModal = () => {
    setError('');
    setIsInviteModalOpen(false);
    setCheckedPermissions([]);
    setEmailList([]);
  };

  const handleOpenConfirmationModal = () => {
    if (checkedPermissions.length > 0 && emailList.length > 0) {
      setIsInviteConfirmationModalOpen(true);
    }
  };

  const handleInvite = () => {
    const emails = emailList.map((emailItem) => emailItem.email);
    dispatch(inviteActions.inviteSubordinateAsync({ emails, permissions: checkedPermissions }));
    setIsInviteModalOpen(false);
  };

  return (
    <>
      <CustomDialog open={open}>
        <Box sx={{ display: 'flex' }} justifyContent="space-between" flexDirection="row">
          <CustomDialogTitle>
            <Typography variant="title4Bold">Invite Subordinates</Typography>
          </CustomDialogTitle>
          <IconButton onClick={handleCloseModal} color="primary">
            <Box component="img" src={CloseIconSvg} data-testid="close-icon" />
          </IconButton>
        </Box>
        <Divider />
        <SubordinateEmailMultiSelect
          emailList={emailList}
          setEmailList={setEmailList}
          error={error}
          setError={setError}
        />
        {!user.isNccUser && (
          <Box sx={{ display: 'flex', flexDirection: 'column', paddingX: '24px' }}>
            <Typography variant="textH6" style={{ marginTop: '16px' }}>
              Permissions
            </Typography>
            <Grid container sx={{ marginTop: '2px', marginBottom: '8px' }}>
              {permissions?.map((permission) => {
                const permissionId = permission.permissionId ?? '';
                const dependencies = permission.dependencies ?? [];
                const isDisabled = dependencies.length > 0 && !dependencies.some((d) => checkedPermissions.includes(d));

                const tooltipDependencies = dependencies
                  .map((d) => permissionMap.get(d))
                  .filter(Boolean) as IPermission[];

                const tooltipMessage =
                  tooltipDependencies.length > 0
                    ? `Requires one of the following permissions: ${tooltipDependencies
                        .map((d) => d.description)
                        .join(', ')}`
                    : '';

                return (
                  <PermissionItem
                    key={permissionId}
                    permissionId={permissionId}
                    description={permission.description ?? ''}
                    isChecked={checkedPermissions.includes(permissionId)}
                    isDisabled={isDisabled}
                    tooltipMessage={tooltipMessage}
                    onCheck={handleCheckboxClick}
                  />
                );
              })}
            </Grid>
          </Box>
        )}

        <Button
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingX: '24px',
            paddingY: '12px',
            backgroundColor: theme.palette.info.light,
            border: `1px solid ${theme.palette.shades.grayBorder}`,
          }}
          disabled={isButtonDisabled || (isUserLimitExceed && !user.isNccUser)}
          onClick={handleOpenConfirmationModal}
        >
          <Typography
            variant="tableheadBold"
            color={
              isButtonDisabled || (isUserLimitExceed && !user.isNccUser)
                ? theme.palette.common.black
                : theme.palette.white.main
            }
            paddingX="12px"
            paddingY="8PX"
            sx={{
              cursor: `${isButtonDisabled || (isUserLimitExceed && !user.isNccUser) ? 'not-allowed' : 'pointer'}`,
              backgroundColor: `${
                isButtonDisabled || (isUserLimitExceed && !user.isNccUser)
                  ? theme.palette.shades.grayBorder
                  : theme.palette.shades.lightBlueColor
              }`,
              borderRadius: '8px',
            }}
          >
            Invite Subordinates
          </Typography>
        </Button>
      </CustomDialog>
      <CustomModal
        data-testid="custom-modal"
        aria-labelledby="custom-modal"
        modal
        open={isInviteConfirmationModalOpen}
        title="Confirm Invites"
        Icon={<Box component="img" src={WarningSvg} />}
        subTitle="Do you confirm, you want to invite following email addresses?"
      >
        <Box display="flex" flexDirection="column" gap="8px" justifyContent="end" alignItems="center" mt={1}>
          <Typography variant="textH6" color={theme.palette.common.black} textAlign="center">
            {emailList.map((item) => item.email).join(', ')}
          </Typography>
          <CommonButtonV1
            variant="contained"
            sx={{ width: '100%', backgroundColor: theme.palette.shades.lightBlueColor, marginTop: '10px' }}
            onClick={handleInvite}
            loading={invitationLoading}
            data-testid="common-button"
          >
            Yes, send Invites
          </CommonButtonV1>
          <CommonButtonV1
            variant="outlined"
            sx={{
              width: '100%',
              color: theme.palette.common.black,
              border: `1px solid ${theme.palette.shades.grayBorder}`,
            }}
            disabled={invitationLoading}
            backgroundColor="transparent"
            onClick={() => setIsInviteConfirmationModalOpen(false)}
          >
            Cancel
          </CommonButtonV1>
        </Box>
      </CustomModal>
    </>
  );
}

export default InvitationModal;
